import { NavigateFunction } from 'react-router'

import { NotificationLevels } from '../notifier/types'
import { AppInstanceInfo, AppProperties } from 'common/types/application'
import { InterfaceFeedback, NodeFeedback, NodeInterfaceFeedback } from 'common/types/model'
import { AppVersionItem } from 'common/types/version'
import {
  FilterComponent,
  IBaseComponent,
  IRenderComponent
} from 'pages/interfaces/components/types'
import { StatusInternalTests } from 'common/types/internalTests'
import { AnalysisResponse } from 'common/types/applicationAnalysis'
import { AgentStatus } from 'store/agent'
import { HtmlInterfaceBroadcastSender } from 'store/interface/types'
import { AgentActivityAction, AgentActivityPhase, AgentActivityTarget } from './agentLiveView'
import {
  InterfaceExtraParams,
  NotificationType,
  SchedulerExtraParams
} from 'common/types/notifications'

export enum MessageType {
  OPENING_APP = 'OPENING_APP',
  SAVING_APP = 'SAVING_APP',
  NODE_BUTTON_FINISH_PROCESSING = 'NODE_BUTTON_FINISH_PROCESSING',
  NODE_DEBUG_INFORMATION = 'NODE_DEBUG_INFORMATION',
  NODE_DEBUG_INFORMATION_BATCH = 'NODE_DEBUG_INFORMATION_BATCH',
  STANDARD_MESSAGE = 'STANDARD_MESSAGE',
  PROGRESS_BAR = 'PROGRESS_BAR',
  KILLED_INSTANCE = 'KILLED_INSTANCE',
  UPDATE_TASK_STATUS = 'UPDATE_TASK_STATUS',
  UPDATE_TASK_LOG = 'UPDATE_TASK_LOG',
  UPDATE_INSTANCE_RESOURCES = 'UPDATE_INSTANCE_RESOURCES',
  SEND_NODE_ERROR = 'SEND_NODE_ERROR',
  SEND_INTERFACE_ERROR = 'SEND_INTERFACE_ERROR',
  SEND_NODE_INTERFACE_ERROR = 'SEND_NODE_INTERFACE_ERROR',
  SEND_NODE_CONSOLE = 'SEND_NODE_CONSOLE',
  APP_UNSAVED_CHANGES = 'APP_UNSAVED_CHANGES',
  LIBRARIES_INSTALL_PROGRESS = 'LIBRARIES_INSTALL_PROGRESS',
  COMPONENTS_THAT_NEED_RENDERING = 'COMPONENTS_THAT_NEED_RENDERING',
  COMPONENT_RESULT = 'COMPONENT_RESULT',
  NODE_INTERFACE_RESULT = 'NODE_INTERFACE_RESULT',
  PURGED_INSTANCE = 'PURGED_INSTANCE',
  PURGED_SESSION = 'PURGED_SESSION',
  ENGINE_DOWN = 'ENGINE_DOWN',
  DOWNLOAD_FILE = 'DOWNLOAD_FILE',
  UPLOAD_FILES = 'UPLOAD_FILES',
  INTERNAL_TESTS_STATUS = 'INTERNAL_TESTS_STATUS',
  REFRESH_CURRENT_INTERFACE = 'REFRESH_CURRENT_INTERFACE',
  REFRESH_CURRENT_INTERFACE_LAYOUT = 'REFRESH_CURRENT_INTERFACE_LAYOUT',
  SCENARIOS_SELECTION_CHANGED = 'SCENARIOS_SELECTION_CHANGED',
  APP_LOCK_INFO = 'APP_LOCK_INFO',
  REFRESH_APP_NOTIFICATIONS = 'REFRESH_APP_NOTIFICATIONS',
  SCHEDULED_TASK_UPDATED = 'SCHEDULED_TASK_UPDATED',
  SCHEDULED_TASK_FINISHED = 'SCHEDULED_TASK_FINISHED',
  FILTER_RESULT = 'FILTER_RESULT',
  FILTER_COMPONENTS_THAT_NEED_RENDERING = 'FILTER_COMPONENTS_THAT_NEED_RENDERING',
  ANALYSIS_RESPONSE = 'ANALYSIS_RESPONSE',
  TRANSLATIONS_PROGRESS = 'TRANSLATIONS_PROGRESS',
  NAVIGATE_TO_SECTION = 'NAVIGATE_TO_SECTION',
  OPEN_APP = 'OPEN_APP',
  INSTANCE_ABOUT_TO_EXPIRE = 'INSTANCE_ABOUT_TO_EXPIRE',
  AGENT_RESPONSE = 'AGENT_RESPONSE',
  AGENT_TRACE_UPDATE = 'AGENT_TRACE_UPDATE',
  REFRESH_DIAGRAM = 'REFRESH_DIAGRAM',
  APP_NOTIFICATION_MESSAGE = 'APP_NOTIFICATION_MESSAGE',
  FILE_OPERATION_COMPLETE = 'FILE_OPERATION_COMPLETE',
  FILE_OPERATION_PROGRESS = 'FILE_OPERATION_PROGRESS',
  OPEN_APP_TASK_RESULT = 'OPEN_APP_TASK_RESULT',
  AGENT_PRESENCE = 'AGENT_PRESENCE',
  AGENT_ACTIVITY = 'AGENT_ACTIVITY',
  APP_INFO_CHANGED = 'APP_INFO_CHANGED',
  APP_CLOSED_BY_AGENT = 'APP_CLOSED_BY_AGENT',
  HTML_INTERFACE_BROADCAST = 'HTML_INTERFACE_BROADCAST',
  CAPTURE_USER_VIEW = 'CAPTURE_USER_VIEW'
}

type BaseSocketMessage = {
  messageType: MessageType
  /** Size in characters of the raw websocket frame this message was parsed from. */
  payloadChars?: number
}

export interface SocketMessage extends BaseSocketMessage {
  title?: string
  message: string
  notLevel: NotificationLevels
  progress?: number
  interfaceId?: string
  result?: IBaseComponent
  closeWhenFinished?: boolean
  autoHideDuration?: number
  extra_params: string | null
}
export interface SocketNotification {
  data: SocketMessage
  navigate?: NavigateFunction
}
export interface RefreshSocketMessage extends BaseSocketMessage {
  instanceId: string
  message: string
  nodeId?: string
  nodes?: string[]
  /** Modules whose diagram changed. Without it, the module on screen is refreshed. */
  moduleIds?: string[]
}

export interface SocketAgentPresenceMessage extends BaseSocketMessage {
  attached: boolean
  expiresIn: number
  clientName?: string | null
}

export interface SocketCaptureUserViewMessage extends BaseSocketMessage {
  requestId: string
  /** How long the tab may wait for widgets still loading before it captures. */
  settleMs?: number
}

export interface SocketAgentActivityMessage extends BaseSocketMessage {
  action: AgentActivityAction
  section: string
  toolId: string
  nodeIds: string[]
  moduleId?: string | null
  interfaceId?: string | null
  /** 'before' announces where the agent is about to work; 'after' reports the change. */
  phase?: AgentActivityPhase
  target?: AgentActivityTarget
  highlightNodeIds?: string[]
  /** Title of the node, module or interface the agent changed. */
  title?: string | null
}

export interface SocketAppInfoChangedMessage extends BaseSocketMessage {
  application: AppInstanceInfo
  /** The session moved to another version, so the page has to load it. */
  versionChanged?: boolean
  /** The new version carries the session's changes, as saving as a new version does. */
  versionKeepsSession?: boolean
}

export interface SocketMessageInternalTests extends BaseSocketMessage {
  statusTests?: StatusInternalTests
}

export interface SocketMessageTranslationsProgress extends BaseSocketMessage {
  progress: number
}

export interface SocketInstanceResources {
  cpu: number
  usedMemory: number
  totalMemory: number
}

export enum actionTypes {
  START_CALC = 'startCalc',
  END_CALC = 'endCalc'
}

// What a performance row measures. Engines that predate the split send no
// `entity`, so consumers fall back to NODE.
export enum debugEntityTypes {
  NODE = 'node',
  COMPONENT = 'component',
  INTERFACE = 'interface'
}

// A single node debug event. The engine batches these, so the event payload is
// separate from the message envelope (instance_id / messageType).
export interface socketModelDebugEvent {
  action: actionTypes
  cumulativeTime: number
  fromDynamic: string
  maxMemory: number
  node: string
  time: number
  title: string
  totalMemory: number
  usedMemory: number
  order: number
  entity?: debugEntityTypes
  // Set on component rows only. `node` still carries the node the component
  // visualizes, which two components can share.
  component?: string
  componentType?: string
}

// Legacy one-event-per-message shape, still sent by older engines.
export interface socketModelDebug extends socketModelDebugEvent {
  instance_id: string
  messageType: MessageType
}

export interface socketModelDebugBatch {
  instance_id: string
  messageType: MessageType
  events: socketModelDebugEvent[]
}

export interface socketModelDebugAndId extends socketModelDebugEvent {
  id: number
}

export enum ScheduleTaskStates {
  PROGRESS = 'PROGRESS',
  INFO = 'INFO',
  WARNING = 'WARNING',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  SUCCESS = 'SUCCESS',
  REVOKED = 'REVOKED',
  STARTED = 'STARTED',
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED'
}

export interface SocketNodeFeedback extends BaseSocketMessage {
  message: NodeFeedback
}

export interface SocketInterfaceFeedback extends BaseSocketMessage {
  message: InterfaceFeedback
}

export interface SocketNodeInterfaceFeedback extends BaseSocketMessage {
  message: NodeInterfaceFeedback
}

export interface SocketInterfaceComponentsList extends BaseSocketMessage {
  interfaceId: string
  componentsList: string[]
}

export interface SocketInterfaceFilterComponentsList extends BaseSocketMessage {
  interfaceId: string
  componentIds: string[]
}

export interface SocketInterfaceLayoutRefreshMessage extends BaseSocketMessage {
  /** The interface that changed. Without it, the interface on screen is refreshed. */
  interfaceId?: string
  interfaceCreated?: boolean
}

export interface SocketInterfaceComponentResult extends BaseSocketMessage {
  interfaceId: string
  result: IRenderComponent
}

export interface SocketInterfaceFilterComponentResult extends BaseSocketMessage {
  interfaceId: string
  result: FilterComponent
}

export interface SocketInstanceStatusMessage extends BaseSocketMessage {
  title: string
  message: string
  instance_id: string
}

export interface SocketAgentMessage extends BaseSocketMessage {
  instanceId: string
  agentCode: string
  chatSessionId: string
  message?: string
  error?: string
  status?: AgentStatus
  metadata?: Record<string, any>
}

export interface SocketTraceUpdateMessage extends BaseSocketMessage {
  instanceId: string
  traceId: string
  traceStatus: string
  span: Record<string, any>
}

export interface InstallationLog {
  log: string
  notLevel: NotificationLevels
}

export interface SocketDownloadMessage extends BaseSocketMessage {
  filePath: string
}

interface UploadedFile {
  uploadedFilePath: string
  targetFileName: string
}

export interface UploaderParams {
  show?: boolean
  targetPath: string
  targetFilename?: string
  allowedExtensions?: string[]
  validationNodeId?: string
  callbackNodeId?: string
  listOfNodeIdsToInvalidate?: string[]
  multiple?: boolean
  uploadText?: string
  uploadedFiles?: UploadedFile[]
}

export interface SocketUploadMessage extends BaseSocketMessage {
  uploadParams: UploaderParams
}

export interface SocketAppInfo extends BaseSocketMessage {
  appIsLocked: boolean
}

export interface SocketAnalysisResponse extends BaseSocketMessage {
  analysisResponse: AnalysisResponse
}

/** A run launched by hand from this session ended. Carries the name and the
 *  final state as data; the sentence is written by the UI so it is translated. */
export interface SocketScheduledTaskFinished extends BaseSocketMessage {
  taskName: string
  state: string
}

/** One scheduled task changed its state. `isOpenApp` is resolved per recipient
 *  by the API, so the payload never carries the task's app path or its name. */
export interface SocketScheduledTaskUpdated extends BaseSocketMessage {
  taskId: number
  state: string | null
  enabled: boolean
  isOpenApp: boolean
}

export interface SocketHtmlInterfaceBroadcast extends BaseSocketMessage {
  event: string
  data: unknown
  targetInterfaceId: string | null
  from: HtmlInterfaceBroadcastSender
}

export enum NavigationSection {
  HOME = 'home',
  CODE = 'code',
  INTERFACES = 'interfaces',
  FILES = 'files'
}

export interface SocketNavigateToSectionMessage extends BaseSocketMessage {
  section: NavigationSection
  params: { [key: string]: any }
}

export interface OpenAppParams {
  folder: string
  version: string | null
  readOnly: boolean
  resources: string | null
  openOnNewInstance: boolean
  showVersionsOnOpen: boolean
  appProperties: AppProperties
  defaultVersion: AppVersionItem
  canWriteInDir: boolean
  interfaceIdToOpenOnStart: string | null
}

export interface SocketOpenAppMessage extends BaseSocketMessage {
  params: OpenAppParams
}

export interface SocketAppNotificationMessage extends BaseSocketMessage {
  notificationType: NotificationType
  message: string
  extraParams?: object | SchedulerExtraParams | InterfaceExtraParams
}

export type AsyncOperationType =
  | 'moveFiles'
  | 'copyFiles'
  | 'downloadFiles'
  | 'zipFiles'
  | 'deleteFiles'
  | 'duplicateFiles'
  | 'copyAppToWorkspace'
  | 'copyAppToTeam'
  | 'copyAppToPublic'

export interface SocketAsyncOperationCompleteMessage extends BaseSocketMessage {
  operationId: string
  operationType: AsyncOperationType
  success: boolean
  result?: string
  error?: string
}

export interface PendingAsyncOperation {
  operationId: string
  operationType: AsyncOperationType
  context?: Record<string, any>
  startTime: number
}

// Sent once open_app_task (pyplan-api) finishes, success or failure — see
// plannings/open_app_async.md, Fase 4. Delivered on the instance's own
// socket connection, so (like OPENING_APP) there's no instanceId field to
// filter on here — the backend's instance_id is routing metadata for the WS
// gateway, not something forwarded to the frontend payload.
export interface SocketOpenAppTaskResultMessage extends BaseSocketMessage {
  success: boolean
  appInfo?: AppInstanceInfo
  error?: string
  logDetails?: string
}

export interface SocketFileOperationProgressMessage extends BaseSocketMessage {
  operationId: string
  operationType: AsyncOperationType
  percent: number
  current: number
  total: number
  fileName: string
}

export interface AsyncOperationProgress {
  operationId: string
  operationType: AsyncOperationType
  percent: number
  current: number
  total: number
  fileName: string
}
